import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Shield, RefreshCw, Package, Clock } from 'lucide-react';
import { PageFAQSchema, useCanonicalUrl } from '../components/seo/SchemaMarkup';

const RETURNS_FAQS = [
  {
    question: 'How long do I have to return an item?',
    answer: 'You have 30 days from the date of delivery to return eligible NinjAthlete apparel items in new, unworn condition with original tags attached.',
  },
  {
    question: 'How do I start a return?',
    answer: 'Visit our Redo-powered returns portal at ninjathlete.com/a/redo/returns, enter your order number and email, select the items you want to return, and follow the on-screen instructions to print your prepaid label.',
  },
  {
    question: 'Is return shipping free?',
    answer: 'If you added Redo Coverage at checkout, return shipping is 100% free. Without coverage, a flat $7.99 return shipping fee is deducted from your refund.',
  },
  {
    question: 'When will I receive my refund?',
    answer: 'Refunds are processed within 5-7 business days after we receive and inspect your returned items. The refund goes back to the original payment method used at checkout.',
  },
  {
    question: 'Can I exchange an item for a different size or color?',
    answer: 'Yes — exchanges are processed through the same returns portal. Select "Exchange" instead of "Refund" and choose your preferred variant. If the new item costs more, you can pay the difference; if less, the difference is refunded.',
  },
  {
    question: 'Which items are not eligible for return?',
    answer: 'Final-sale items, digital products (training plans, e-books, PDFs), and any product with the "Final Sale" badge on its page are non-returnable. Worn, washed, or damaged items are also not eligible.',
  },
];

export default function ReturnsPage() {
  useCanonicalUrl();
  const REDO_PORTAL_URL = 'https://ninjathlete.com/a/redo/returns';

  return (
    <main className="pt-20 min-h-screen">
      <PageFAQSchema
        faqs={RETURNS_FAQS}
        pageName="NinjAthlete Returns & Exchanges"
        pagePath="/returns"
        scriptId="returns-faq-schema"
      />
      <div className="container-ninja section-spacing max-w-3xl">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-sm mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
            <RefreshCw className="w-8 h-8 text-emerald-400" />
          </div>
          <h1
            className="text-4xl sm:text-5xl uppercase tracking-wider text-white mb-4"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="returns-heading"
          >
            Returns & Exchanges
          </h1>
          <p className="text-zinc-400 text-base max-w-lg mx-auto">
            We want you to love your NinjAthlete gear. If something doesn't work out, our returns process is simple and hassle-free.
          </p>
        </div>

        {/* Start Return CTA */}
        <div className="bg-zinc-900 border border-zinc-800 p-8 text-center mb-10">
          <h2
            className="text-2xl uppercase tracking-wider text-white mb-3"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Ready to start a return?
          </h2>
          <p className="text-zinc-500 text-sm mb-6">
            Use our returns portal powered by Redo to submit your return in minutes.
          </p>
          <a
            href={REDO_PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#E63946] hover:bg-[#c62d39] text-white uppercase tracking-widest font-bold transition-all duration-300"
            style={{ fontFamily: 'Oswald, sans-serif' }}
            data-testid="start-return-btn"
          >
            Start a Return <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {/* Policy Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {[
            {
              icon: Clock,
              title: '30-Day Return Window',
              desc: 'Return eligible items within 30 days of delivery for a full refund or exchange.',
            },
            {
              icon: Shield,
              title: 'Redo Coverage',
              desc: 'Added Redo at checkout? Enjoy free return shipping — no costs to you.',
            },
            {
              icon: Package,
              title: 'Easy Process',
              desc: 'Submit your return online, print the prepaid label, and drop it off. We handle the rest.',
            },
            {
              icon: RefreshCw,
              title: 'Exchanges Welcome',
              desc: 'Need a different size or color? Swap it out through the returns portal.',
            },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-zinc-900/60 border border-zinc-800 p-6">
              <Icon className="w-5 h-5 text-emerald-400 mb-3" />
              <h3 className="text-white text-sm font-semibold mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                {title}
              </h3>
              <p className="text-zinc-500 text-xs leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        <section className="mt-12 border-t border-zinc-900 pt-10" data-testid="returns-faq">
          <h2
            className="text-2xl uppercase tracking-wider text-white mb-6 text-center"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Frequently Asked Questions
          </h2>
          <div className="space-y-2">
            {RETURNS_FAQS.map((faq, i) => (
              <details
                key={faq.question}
                className="group border border-zinc-800 bg-zinc-900/40"
                data-testid={`returns-faq-item-${i}`}
              >
                <summary className="cursor-pointer px-5 py-4 text-sm text-zinc-200 font-medium flex items-center justify-between hover:bg-zinc-900/70 transition-colors">
                  <span>{faq.question}</span>
                  <span className="text-zinc-500 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <div className="px-5 pb-4 text-zinc-400 text-sm leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </section>

        <div className="text-center text-zinc-600 text-xs mt-10">
          Questions? <Link to="/contact" className="text-zinc-400 hover:text-white transition-colors underline">Contact us</Link>
        </div>
      </div>
    </main>
  );
}
